import type { Rng } from '../core/rng';
import type { Vec3 } from '../geo/types';
import type { AudioVoices } from './audioVoices';
import { spatialSound, type SoundEmitter, type SpatialSound } from './spatialAudio';

/*
 * The oars of a rowing boat. A stroke has four sounds, and none of them is a hiss in step with
 * the boat's speed, which is what one burst of noise for every two metres made sounded like, an
 * engine. The blades dip with a soft plop to either side, the water runs past them through the
 * pull as a swell that grows and dies, the oars knock in their tholes at the finish, and a few
 * drops fall from the lifted blades. The rower pulls about 22 strokes a minute at ease and about
 * 32 pulling hard. Timbres and timings are estimates, not recordings.
 */
export const ROWING = {
  /** Below this the boat only drifts and no oar is in the water, in metres a second. */
  minSpeed: 0.25,
  /** The speeds of rowing at ease and pulling hard (BOAT.rowSpeed, BOAT.pullSpeed). */
  easySpeed: 1.6,
  hardSpeed: 3,
  easyPeriod: 2.7,
  hardPeriod: 1.9,
  /** A boat losing way faster than this is gliding with its oars at rest, in metres a second squared. */
  glideDecel: 0.15,
  /** Where a blade meets the water: out to the side and a little ahead of the bench, in metres. */
  bladeOut: 1.6,
  bladeAhead: 0.6,
  reach: 9,
  level: 0.36,
} as const;

/** A stroke as it begins: how hard it is pulled, 0 at ease to 1, and the way the boat is going. */
export interface Stroke {
  readonly effort: number;
  readonly period: number;
  /** Unit vector of the boat's way on the world's xz plane. */
  readonly way: readonly [number, number];
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** The cadence of the oars, read off the way the boat makes. Seeks and time gaps start no stroke. */
export class RowingCadence {
  private last: Vec3 | null = null;
  private time = 0;
  private speed = 0;
  private next = 0;

  reset(): void {
    this.last = null;
    this.speed = 0;
    this.next = 0;
  }

  update(position: Vec3, now: number): Stroke | null {
    const before = this.last;
    const dt = now - this.time;
    this.last = [...position];
    this.time = now;
    if (!before || dt <= 0 || dt > 0.5) {
      this.speed = 0;
      return null;
    }
    const [dx, dz] = [position[0] - before[0], position[2] - before[2]];
    const distance = Math.hypot(dx, dz);
    if (distance > Math.max(3, dt * 8)) {
      this.speed = 0;
      return null;
    }
    const speed = distance / dt;
    const gliding = (speed - this.speed) / dt < -ROWING.glideDecel;
    this.speed = speed;
    if (speed < ROWING.minSpeed || gliding || now < this.next) return null;
    const effort = clamp01((speed - ROWING.easySpeed) / (ROWING.hardSpeed - ROWING.easySpeed));
    const period = ROWING.easyPeriod + (ROWING.hardPeriod - ROWING.easyPeriod) * effort;
    this.next = now + period;
    return { effort, period, way: [dx / distance, dz / distance] };
  }
}

function blade(position: Vec3, way: readonly [number, number], side: -1 | 1): SoundEmitter {
  // To the right of the way (x, z) lies (-z, x).
  return {
    id: side < 0 ? 'oar-port' : 'oar-starboard',
    kind: 'water',
    at: [
      position[0] + way[0] * ROWING.bladeAhead - way[1] * side * ROWING.bladeOut,
      position[1] - 1.2,
      position[2] + way[1] * ROWING.bladeAhead + way[0] * side * ROWING.bladeOut,
    ],
    reach: ROWING.reach,
    level: ROWING.level,
  };
}

/* One oar through one stroke. */
function soundOar(
  voices: AudioVoices,
  sound: SpatialSound,
  pull: number,
  force: number,
  late: number,
  rng: Rng
): void {
  // The catch: the blade dips.
  voices.grain(
    { hz: rng.range(380, 460), to: 240, seconds: 0.2, weight: 0.5 * force, delay: late },
    sound,
    1.3
  );
  voices.tone({ hz: 150, to: 85, seconds: 0.1, weight: 0.14 * force, delay: late }, sound);
  // The pull: water runs past the blade, growing and dying away.
  voices.grain(
    {
      hz: rng.range(520, 640),
      to: 340,
      seconds: pull + 0.35,
      attack: pull * 0.55,
      weight: 0.3 * force,
      delay: late + 0.06,
    },
    sound,
    0.6
  );
  // The finish: the oar knocks in its tholes.
  voices.tone(
    {
      hz: rng.range(170, 200),
      to: 140,
      seconds: 0.07,
      weight: 0.11,
      delay: late + pull,
      wave: 'triangle',
    },
    sound
  );
  soundDrops(voices, sound, late + pull, rng);
}

/* The recovery: drops fall from the lifted blade. */
function soundDrops(voices: AudioVoices, sound: SpatialSound, from: number, rng: Rng): void {
  const drops = 2 + Math.floor(rng.range(0, 3));
  for (let i = 0; i < drops; i++) {
    voices.grain(
      {
        hz: rng.range(1900, 3100),
        seconds: 0.05,
        weight: rng.range(0.04, 0.08),
        delay: from + rng.range(0.12, 0.75),
      },
      sound,
      9
    );
  }
}

/** One stroke of both oars, heard from the bench. */
export function soundStroke(
  voices: AudioVoices,
  stroke: Stroke,
  position: Vec3,
  yaw: number,
  rng: Rng
): void {
  const pull = stroke.period * 0.42;
  const force = 0.75 + 0.25 * stroke.effort;
  for (const side of [-1, 1] as const) {
    const sound = spatialSound(blade(position, stroke.way, side), position, yaw);
    // The two blades never dip in quite the same instant.
    soundOar(voices, sound, pull, force, side < 0 ? 0 : rng.range(0.015, 0.05), rng);
  }
}
